import { useEffect, useMemo, useRef, useState } from "react";
import { X, Save, Trash2, Check, X as XIcon } from "lucide-react";
import { Product } from "../../../types/product.types";
import { useTranslation } from "react-i18next";
import { EditableText } from "../Common/EditableText";
import { useDispatch } from "react-redux";
import {
  updateProduct,
  deleteProduct,
} from "../../../redux/slices/productSlice";

export function ProductDetailsOverlay({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [form, setForm] = useState<Product>(product);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const deletePopupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setForm(product);
  }, [product]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (!showDeleteConfirm) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        deletePopupRef.current &&
        !deletePopupRef.current.contains(event.target as Node)
      ) {
        setShowDeleteConfirm(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDeleteConfirm]);

  const hasChanges = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(product),
    [form, product]
  );

  const handleSave = () => {
    if (!hasChanges) return;
    dispatch(updateProduct({ product: form }) as any);
    onClose();
  };

  const handleDelete = () => {
    dispatch(deleteProduct({ productID: product._id }) as any);
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative mt-20 w-full max-w-3xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-semibold dark:text-primaryTextDark text-primaryText">
            {t("Product Details")}
          </h2>

          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setShowDeleteConfirm((prev) => !prev)}
              className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition"
            >
              <Trash2 size={18} className="text-red-600" />
            </button>

            {showDeleteConfirm && (
              <div
                ref={deletePopupRef}
                className="absolute right-0 top-12 z-50 w-64 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <p className="text-sm mb-4 dark:text-primaryTextDark text-primaryText">
                  {t("Do you want to delete this item?")}
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    <XIcon size={18} />
                  </button>

                  <button
                    onClick={handleDelete}
                    className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30"
                  >
                    <Check size={18} className="text-red-600" />
                  </button>
                </div>
              </div>
            )}

            <button
              disabled={!hasChanges}
              onClick={handleSave}
              className={`p-2 rounded-full transition ${
                hasChanges
                  ? "hover:bg-gray-200 dark:hover:bg-gray-700"
                  : "opacity-40 cursor-not-allowed"
              }`}
            >
              <Save size={18} />
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 dark:border-gray-700 dark:hover:bg-gray-700 transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scroll">
          <img
            src={form.image}
            alt={form.name}
            className="w-full h-64 object-cover rounded-lg"
          />

          <div className="flex gap-2 items-start">
            <span className="font-semibold w-28 dark:text-primaryTextDark text-primaryText">
              {t("Name")}:
            </span>
            <EditableText
              value={form.name}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, name: value }))
              }
            />
          </div>

          <div className="flex gap-2 items-start">
            <span className="font-semibold w-28 dark:text-primaryTextDark text-primaryText">
              {t("Description")}:
            </span>
            <EditableText
              value={form.description}
              multiline
              onChange={(value) =>
                setForm((prev) => ({ ...prev, description: value }))
              }
            />
          </div>

          <div className="flex gap-2 items-center">
            <span className="font-semibold w-28 dark:text-primaryTextDark text-primaryText">
              {t("Price")}:
            </span>
            <EditableText
              value={form.price}
              onChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  price: Number(value) || 0,
                }))
              }
            />
          </div>

          <div className="flex gap-2 items-center">
            <span className="font-semibold w-28 dark:text-primaryTextDark text-primaryText">
              {t("In Stock")}:
            </span>
            <EditableText
              value={form.inStock ?? ""}
              onChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  inStock: Number(value) || 0,
                }))
              }
            />
          </div>

          <div className="flex gap-2 items-start">
            <span className="font-semibold w-28 dark:text-primaryTextDark text-primaryText">
              {t("Tags")}:
            </span>
            <EditableText
              value={form.tags.join(" ")}
              onChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  tags: value.split(" ").filter(Boolean),
                }))
              }
            />
          </div>

          <div className="flex gap-2 items-center">
            <span className="font-semibold w-28 dark:text-primaryTextDark text-primaryText">
              {t("Product ID")}:
            </span>
            <span className="dark:text-primaryTextDark text-primaryText">
              {product._id}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
